/**
 * A5 — the event's v2 upload-network authority editor (S9), beside the legacy
 * UploadPolicyCard in EventTab's right column. This governs the CANONICAL v2
 * lane's four-mode ladder (what network each artifact class MAY use); the
 * legacy card governs the v1 tier scheduler. Backed by:
 *   - GET /api/admin/events/{id}/v2-upload-policy   (session cookie + CSRF)
 *   - PUT /api/admin/events/{id}/v2-upload-policy   (session cookie + CSRF)
 *
 * Auth: ported from the source's `services/adminBearer.ts` bearer scheme onto
 * this target's session-cookie auth. `api.ts`'s axios client already attaches
 * the double-submit `X-Tuvion-CSRF` header on every unsafe method (see
 * `services/csrf.ts::UNSAFE_METHODS`), so — unlike the read-only v2 player —
 * this card's PUT gets the CSRF header for free from the shared client; no
 * bearer, no manual header wiring here.
 *
 * No fan-out: phones pick the grant up on their next AUTHENTICATED arm-time
 * capture-settings fetch, inside the mirror's 6 h freshness window. Every
 * accepted write bumps the revision — a same-mode re-push is deliberately
 * distinguishable from a stale replay on the phone.
 *
 * CROSS-EVENT SAFETY is structural (S9 review R3): EventTab mounts this card
 * with `key={eventId}`, so `eventId` is CONSTANT for an instance's whole life —
 * there is no render window in which one event's authority can appear under
 * another's identity, and every closure in this file is single-event by
 * construction. What key-remount cannot solve is SERVER-SIDE write ordering:
 * a PUT issued by a dead instance may still be in flight when the operator
 * returns to the same event and saves again, and the older write could commit
 * last. [chainEventOperation] serializes every GET/PUT PER EVENT at module
 * level (a returning instance's load waits behind a dead instance's write; a
 * second save waits behind the first), and the server-side expected_revision
 * CAS fences what chaining cannot: a transport-rejected PUT that still
 * commits server-side dies on its stale expectation — and when such a ghost
 * commits FIRST, the later save receives 409 and reconciles to the current
 * authority for the operator to review and re-save. The UI and the backend
 * always converge on one row; a survived ghost surfaces as a reconciliation,
 * never as a silent clobber.
 *
 * Per-device CAPS (narrow-only) are shown read-only when present; editing
 * them stays API-only until the cap workflow is designed (spec §14).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Radio, RotateCcw, Save } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/services/api';
import type { V2UploadPolicyMode, V2UploadPolicyRow, V2UploadPolicyState } from '@/types';

// Grant semantics mirror the phone exactly (capture V2UploadPolicy + V2TrafficClass):
// these are MAXIMUM grants — the driver's local backfill setting can only narrow them.
const MODES: Array<{ value: V2UploadPolicyMode; title: string; hint: string }> = [
  {
    value: 'wifi_only',
    title: 'Wi-Fi only (most conservative)',
    hint: 'Every v2 upload waits for an unmetered link.',
  },
  {
    value: 'audio_only',
    title: 'Audio may use cellular',
    hint: 'All sound — live and backlog — may use cellular; every video artifact waits for Wi-Fi.',
  },
  {
    value: 'live_video',
    title: 'Live video may use cellular',
    hint: "Sound plus the active session's newest video segment may use cellular; video gaps and older backlog still wait for Wi-Fi.",
  },
  {
    value: 'finish_all',
    title: 'Everything may use cellular',
    hint: 'End-of-day "get it all off the phones" — backlog included. Watch the data budget.',
  },
];

/**
 * Per-event async chain, MODULE level so it outlives card instances: appending an
 * operation guarantees it runs strictly after every previously-appended operation for
 * the same event — even ones issued by an unmounted instance. Failures don't break the
 * chain (each link settles); the map entry is cleared when its chain drains.
 */
const eventOperationChains = new Map<number, Promise<void>>();

function chainEventOperation<T>(eventId: number, operation: () => Promise<T>): Promise<T> {
  const previous = eventOperationChains.get(eventId) ?? Promise.resolve();
  const result = previous.then(operation, operation);
  const link = result.then(
    () => undefined,
    () => undefined,
  );
  eventOperationChains.set(eventId, link);
  link.finally(() => {
    if (eventOperationChains.get(eventId) === link) eventOperationChains.delete(eventId);
  });
  return result;
}

type Phase = 'loading' | 'ready' | 'error';

interface Props {
  eventId: number | null;
}

export const V2UploadPolicyCard: React.FC<Props> = ({ eventId }) => {
  const [state, setState] = useState<V2UploadPolicyState | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [mode, setMode] = useState<V2UploadPolicyMode>('wifi_only');
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  // Guards StrictMode's doubled effect and the Retry button re-running the loader:
  // only the newest load attempt within THIS instance settles.
  const loadAttemptRef = useRef(0);

  const load = useCallback(() => {
    if (eventId == null) return;
    const attempt = ++loadAttemptRef.current;
    setPhase('loading');
    setErrorText(null);
    void (async () => {
      try {
        // Chained: waits behind any still-in-flight PUT for this event (including one
        // issued by a previous, unmounted instance), so the loaded state reflects it.
        const data = await chainEventOperation(eventId, () => api.getV2UploadPolicyState(eventId));
        if (loadAttemptRef.current !== attempt) return;
        setState(data);
        setMode(data.event?.mode ?? data.default_mode);
        setPhase('ready');
      } catch (e) {
        if (loadAttemptRef.current !== attempt) return;
        const msg =
          (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data
            ?.detail ??
          (e as { message?: string })?.message ??
          'failed to load';
        setErrorText(msg);
        setPhase('error');
      }
    })();
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const savedMode: V2UploadPolicyMode = state?.event?.mode ?? state?.default_mode ?? 'wifi_only';
  const dirty = phase === 'ready' && mode !== savedMode;
  const unconfigured = phase === 'ready' && state?.event == null;

  const handleSave = async () => {
    if (phase !== 'ready' || eventId == null || saving) return;
    setSaving(true);
    setErrorText(null);
    // The CAS fence (S9 review R4): client-side chaining orders ISSUANCE, but a
    // transport-rejected PUT may still commit server-side later. The write applies
    // only while the row's revision equals what this card last saw (0 = no row yet);
    // a ghost carrying a stale expectation dies in the server's atomic upsert.
    const expectedRevision = state?.event?.revision ?? 0;
    try {
      // Chained: strictly after every earlier operation for this event, so two saves
      // (even across a leave-and-return) can never interleave client-side either.
      const row = await chainEventOperation(eventId, () =>
        api.setV2UploadPolicy(eventId, { mode, expected_revision: expectedRevision }),
      );
      setState((previous) => (previous ? { ...previous, event: row } : previous));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail as { reason?: string; current?: V2UploadPolicyRow | null } | undefined;
      if (status === 409 && detail?.reason === 'revision_conflict') {
        // Reconcile in one trip: the server handed back the CURRENT row.
        setState((previous) =>
          previous ? { ...previous, event: detail.current ?? null } : previous,
        );
        setMode(detail.current?.mode ?? state?.default_mode ?? 'wifi_only');
        setErrorText('changed elsewhere — reloaded the current authority; review and save again');
      } else {
        const msg =
          (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data
            ?.detail ??
          (e as { message?: string })?.message ??
          'save failed';
        setErrorText(typeof msg === 'string' ? msg : 'save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  // The W3C radio keyboard pattern: arrows move selection, roving tabIndex.
  const handleRadioKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (saving) return;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % MODES.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + MODES.length) % MODES.length;
    }
    if (nextIndex == null) return;
    event.preventDefault();
    setMode(MODES[nextIndex].value);
    const group = (event.currentTarget as HTMLElement).parentElement;
    (group?.children[nextIndex] as HTMLElement | undefined)?.focus();
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <Radio className="h-4 w-4 text-accent" />
          <h2 className="text-base font-semibold">Live-sound upload authority</h2>
          {dirty && (
            <span className="rounded bg-accent/20 px-1 text-[10px] text-accent">unsaved</span>
          )}
          {phase === 'ready' && state?.event && (
            <span className="ml-auto text-[10px] text-fg-secondary">rev {state.event.revision}</span>
          )}
        </div>
        {eventId == null && (
          <p className="text-xs text-fg-secondary">Select an event to edit its v2 upload authority.</p>
        )}
        {eventId != null && phase === 'loading' && (
          <div className="flex items-center gap-2 text-xs text-fg-secondary">
            <Loader2 className="h-3 w-3 animate-spin" /> loading…
          </div>
        )}
        {eventId != null && phase === 'error' && (
          // Unknown authority is never painted as a selectable "Wi-Fi only".
          <div className="space-y-2">
            <p role="alert" className="text-xs text-destructive">
              {errorText ?? 'failed to load'}
            </p>
            <Button size="sm" variant="outline" onClick={load} className="h-7 px-2 text-xs">
              <RotateCcw className="mr-1 h-3 w-3" /> Retry
            </Button>
          </div>
        )}
        {eventId != null && phase === 'ready' && (
          <div className="space-y-3">
            <p className="text-[11px] text-fg-secondary">
              The widest network the new (v2) recording lane may use, per artifact class —
              drivers can locally narrow it, never widen. Phones apply changes at their next
              arm.
              {unconfigured && ' Not set for this event, so phones get the highlighted default.'}
            </p>
            <div role="radiogroup" aria-label="v2 upload authority mode" className="space-y-2">
              {MODES.map((option, index) => {
                const selected = option.value === mode;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    tabIndex={selected ? 0 : -1}
                    disabled={saving}
                    onClick={() => setMode(option.value)}
                    onKeyDown={(event) => handleRadioKeyDown(event, index)}
                    className={`w-full rounded-md border p-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      selected
                        ? 'border-accent bg-accent/10'
                        : 'border-border bg-bg-elevated/40 hover:bg-bg-elevated/60'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-3 w-3 rounded-full border-2 ${
                          selected ? 'border-accent bg-accent' : 'border-border'
                        }`}
                      />
                      <span className="text-sm font-medium">{option.title}</span>
                    </div>
                    <p className="mt-1 pl-5 text-[10px] text-fg-secondary">{option.hint}</p>
                  </button>
                );
              })}
            </div>

            {state && state.device_caps.length > 0 && (
              <div className="rounded-md border border-border bg-bg-elevated/20 p-2 text-[10px] text-fg-secondary">
                <div className="mb-1 font-medium">Per-device caps (narrow-only)</div>
                <div className="grid grid-cols-2 gap-1">
                  {state.device_caps.map((cap) => (
                    <React.Fragment key={cap.device_id ?? ''}>
                      <span className="font-mono">{cap.device_id}</span>
                      <span className="font-mono">
                        {cap.mode} · rev {cap.revision}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {errorText && (
              <p role="alert" className="text-xs text-destructive">
                {errorText}
              </p>
            )}

            <div className="flex items-center justify-end">
              <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 px-2 text-xs">
                {saving ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Save className="mr-1 h-3 w-3" />
                )}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default V2UploadPolicyCard;
